import { useCallback, useEffect, useState } from 'react';

import { InputField, Select, Separator, toast } from '@raystack/apsara';
import { Button, Flex, Text } from '@raystack/apsara';
import { Modal } from '@studio/components';
import { AxiosError } from 'axios';
import { mutate } from 'swr';

import { apiClient } from '@src/clients';
import {
  ConstantOrderItemImageType,
  GeojsonGeneralGeometry
} from '@src/clients/clerk/api';
import { CreateNewProjectButton } from '@src/components/CreateNewProject';
import { OrderDeskUseCase } from '@src/containers/OrderDesk/OrderDeskUseCase';
import { For } from '@src/hoc';

import styles from './AddToCartPopup.module.css';

interface AddToCartPopup {
  open: boolean;
  setOpen: (value: boolean) => void;
  setShowCreateProject: (value: boolean) => void;
  id: string;
  onClose: VoidFunction;
  price: string | number;
  projects: { id: string; title: string }[];
  geometry: GeojsonGeneralGeometry;
}

type ButtonProps = React.ComponentProps<typeof Button>;

const AddToCartPopupContent = ({
  name,
  usecase,
  project_id,
  projects,
  onChange,
  closeAddToCartPopup,
  setShowCreateProject
}: {
  name: string;
  usecase: string;
  project_id: string;
  projects: AddToCartPopup['projects'];
  onChange: (key: 'name' | 'project_id' | 'usecase', value: string) => void;
  closeAddToCartPopup: () => void;
  setShowCreateProject: (value: boolean) => void;
}) => {
  const options = [
    {
      label: 'Name',
      comp: (
        <InputField
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange('name', e.target.value)
          }
          data-test-id={'name'}
        />
      )
    },
    {
      label: 'Use case',
      comp: (
        <OrderDeskUseCase
          onValueChange={value => onChange('usecase', value)}
          value={usecase}
        />
      )
    },
    {
      label: 'Project',
      comp: (
        <Select
          onValueChange={value => onChange('project_id', value)}
          value={project_id}
        >
          <Select.Trigger>
            <Select.Value placeholder={'Provide a project for this order'} />
          </Select.Trigger>

          <Select.Content>
            <Select.Group>
              <For each={projects}>
                {option => (
                  <Select.Item key={option.id} value={option.id}>
                    {option.title}
                  </Select.Item>
                )}
              </For>

              <CreateNewProjectButton
                data-test-id='create-new-project'
                onClick={() => {
                  closeAddToCartPopup();
                  setShowCreateProject?.(true);
                }}
              />
            </Select.Group>
          </Select.Content>
        </Select>
      )
    }
  ];

  return (
    <For each={options}>
      {d => (
        <Flex direction='column' gap='extra-small'>
          <Text variant='secondary' weight={500} size={1}>
            {d.label}
          </Text>

          {d.comp}
        </Flex>
      )}
    </For>
  );
};

export const AddToCartPopup = ({
  id,
  open,
  setOpen,
  price,
  projects,
  geometry,
  setShowCreateProject
}: AddToCartPopup) => {
  const [isLoading, setIsLoading] = useState(false);

  const [values, setValues] = useState({
    name: '',
    project_id: '',
    usecase: 'agriculture'
  });

  const onChange = useCallback((key: keyof typeof values, value: string) => {
    setValues(d => ({ ...d, [key]: value }));
  }, []);

  const onClose = () => {
    setOpen(false);
    setTimeout(() => {
      document.body.style.pointerEvents = 'auto';
    }, 50);
  };

  const onAddToCart = async () => {
    try {
      setIsLoading(true);

      await apiClient.clerkApi.v1OrderItemsCreate({
        recurrence: 'NOT_APPLICABLE',
        usecase: values.usecase,
        project_id: values.project_id,
        name: values.name,
        image_type: ConstantOrderItemImageType['OrderItemImageTypeVNIR'],
        payment_mode: 'FULL',
        images: [{ image_id: id, geometry }],
        delivery_speed: 'STANDARD'
      });

      await mutate('in-cart');
      onClose();
    } catch (err: unknown) {
      console.error(err);
      toast.error('Validation Error!', {
        description: (err as AxiosError).response?.data.error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const actions: ButtonProps[] = [
    {
      children: 'Cancel',
      variant: 'outline',
      color: 'neutral',
      onClick: onClose,
      disabled: isLoading
    },
    {
      children: 'Add to cart',
      variant: 'solid',
      color: 'accent',
      loading: isLoading,
      loaderText: 'Add to cart',
      disabled:
        !(
          values.name.length > 0 &&
          values.usecase.length > 0 &&
          values.project_id.length > 0
        ) || isLoading,
      onClick: onAddToCart
    }
  ];

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        document.body.style.pointerEvents = 'auto';
      }, 50);
    }
  }, [open]);

  return (
    <Modal
      show={open}
      onClose={onClose}
      title='Add to cart'
      footer={
        <>
          <Separator />
          <Flex
            gap='medium'
            align='center'
            justify='between'
            className={styles.actions}
          >
            <Text size={5}>${price}</Text>
            <Flex gap='medium' align='center'>
              <For each={actions}>
                {d => (
                  <Button
                    variant={d.variant}
                    color={d.color}
                    disabled={d.disabled}
                    loading={d.loading}
                    loaderText={d.loaderText}
                    onClick={d.onClick}
                    data-test-id={`add-to-cart-popup`}
                  >
                    {d.children}
                  </Button>
                )}
              </For>
            </Flex>
          </Flex>
        </>
      }
    >
      <AddToCartPopupContent
        projects={projects}
        onChange={onChange}
        {...values}
        data-test-id='add-to-cart-popup-content'
        closeAddToCartPopup={() => setOpen(false)}
        setShowCreateProject={setShowCreateProject}
      />
    </Modal>
  );
};
